/**
 * Page-agnostic voice command hook.
 *
 * Lightweight cousin of useVoiceScoring (which is tightly coupled to
 * the ball-by-ball state machine). This one is for "speak to fill the
 * form" screens — Toss, Playing XI, Squad, Match Add, Player Add, etc.
 *
 * Contract:
 *   const v = useVoicePage({ enabled, parser, hotkey });
 *   <button onClick={v.toggle}>{v.listening ? 'Stop' : 'Start'}</button>
 *   <span>{v.transcript}</span>
 *
 * `parser` is page-specific: receives the cleaned transcript and a set
 * of handlers (registered separately) and is expected to:
 *   - dispatch into the handler set
 *   - return a short "what I just did" label for the UI
 *   - return null/undefined for unrecognised phrases
 *
 * The hook owns the SpeechRecognition lifecycle. Callers own the
 * parser + handler wiring — which is the only thing that differs
 * page-to-page.
 *
 * Founder ask 2026-05-26: "We can also create voice commands for
 * creating matches, series, competitions, toss, squad selection,
 * playing 11 etc. … incorporate the same in player creation too."
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";


// Browser Web Speech API. Same shim as useVoiceScoring — Chrome and
// Edge expose it under the webkit prefix; Safari unsupported, Firefox
// no.
function getRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}


export function useVoicePage({
  parser,
  enabled = true,
  hotkey = "v",         // press V to toggle; pass null to disable
  lang = "en-IN",       // Indian English STT model
}) {
  const Recognition = useMemo(getRecognitionCtor, []);
  const supported = Boolean(Recognition);

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [lastApplied, setLastApplied] = useState(null);  // { ts, label } or null
  const [error, setError] = useState("");

  // Refs survive across re-renders. `recognitionRef` holds the singleton
  // STT instance; `wantsListenRef` is what the user asked for (the
  // browser will sometimes auto-stop on silence — we re-start unless
  // the operator hit the mic).
  const recognitionRef = useRef(null);
  const wantsListenRef = useRef(false);
  const parserRef = useRef(parser);
  parserRef.current = parser;

  // Initialise SpeechRecognition lazily — some browsers refuse to
  // construct it until after a user gesture, but recreating it every
  // toggle is fine and avoids permission-flicker.
  const ensureRecognition = useCallback(() => {
    if (!Recognition) return null;
    if (recognitionRef.current) return recognitionRef.current;

    const rec = new Recognition();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      const text = (final || interim).trim();
      setTranscript(text);

      // Only DISPATCH on final (committed) results. Interim is for the
      // visible "I'm listening, here's what I think you're saying"
      // bubble. Without this gate every keystroke-of-speech would fire
      // the parser, which on a noisy stage means dozens of spurious
      // partial commands.
      if (final && parserRef.current) {
        try {
          const label = parserRef.current(final.trim());
          if (label) setLastApplied({ ts: Date.now(), label });
        } catch (exc) {
          // Parser bugs shouldn't kill the listen loop. Surface, then
          // continue — operator can re-speak.
          console.error("voice parser threw:", exc);
        }
      }
    };

    rec.onerror = (event) => {
      // "no-speech" + "aborted" are normal silence/stop events — don't
      // shout. "not-allowed" is the only one worth surfacing.
      if (event.error === "not-allowed") {
        setError("Microphone permission denied. Allow mic access in browser settings.");
        wantsListenRef.current = false;
        setListening(false);
      } else if (event.error && event.error !== "no-speech" && event.error !== "aborted") {
        setError(`Voice error: ${event.error}`);
      }
    };

    rec.onend = () => {
      // The browser auto-stops on silence even with continuous=true.
      // If the operator still wants to listen, restart. Otherwise
      // settle into the "stopped" state.
      if (wantsListenRef.current) {
        try { rec.start(); } catch { /* race: already started */ }
      } else {
        setListening(false);
      }
    };

    recognitionRef.current = rec;
    return rec;
  }, [Recognition, lang]);

  const start = useCallback(() => {
    if (!supported) {
      setError("Voice not supported in this browser. Use Chrome or Edge.");
      return;
    }
    setError("");
    const rec = ensureRecognition();
    if (!rec) return;
    wantsListenRef.current = true;
    try {
      rec.start();
      setListening(true);
    } catch {
      // start() throws if already listening — that's fine.
      setListening(true);
    }
  }, [supported, ensureRecognition]);

  const stop = useCallback(() => {
    wantsListenRef.current = false;
    setListening(false);
    const rec = recognitionRef.current;
    if (!rec) return;
    try { rec.stop(); } catch { /* idempotent */ }
  }, []);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  // Global hotkey — same UX as the BBP "V" toggle. Skipped when typing
  // into an input/textarea/select/contenteditable so dictation in form
  // fields doesn't fight with the voice toggle.
  useEffect(() => {
    if (!hotkey || !enabled || !supported) return undefined;
    const onKey = (e) => {
      if ((e.key || "").toLowerCase() !== hotkey.toLowerCase()) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.target?.isContentEditable) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hotkey, enabled, supported, toggle]);

  // Stop listening on unmount so the mic indicator goes away when the
  // operator navigates to a different page.
  useEffect(() => {
    return () => {
      wantsListenRef.current = false;
      try { recognitionRef.current?.stop(); } catch { /* ignore */ }
    };
  }, []);

  // External path for non-Web-Speech transcripts. Used by the Whisper
  // PTT button — get a transcript from Groq, run it through the SAME
  // parser this hook owns. Keeps "what does an utterance do" in one
  // place (the parser) so PTT and continuous modes share behaviour.
  const applyTranscript = useCallback((text) => {
    if (!text || !parserRef.current) return;
    setTranscript(text);
    try {
      const label = parserRef.current(text.trim());
      if (label) setLastApplied({ ts: Date.now(), label });
    } catch (exc) {
      console.error("voice parser threw (PTT path):", exc);
    }
  }, []);

  return {
    supported,
    listening,
    transcript,
    lastApplied,
    error,
    start,
    stop,
    toggle,
    applyTranscript,
  };
}
